import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Select from 'react-select';
import { Control, DropdownIndicator, Menu, SelectContainer, ValueContainer, Option } from './CustomSelectComponents';

import './Select.custom.scss';
import './Select.theme.scss';

// eslint-disable-next-line react-refresh/only-export-components
export const SelectTypes = { Dropdown: 'dropdown', Tooltip: 'tooltip' };

// eslint-disable-next-line max-lines-per-function
export default function RxSelect(props) {
  const {
    e2eName,
    value,
    defaultValue,
    type,
    placeholder,
    options,
    tooltipAlign,
    getOptionValue,
    getOptionLabel,
    customOptionRenderer,
    customValueRenderer,
    customIsOptionSelected,
    controlShouldRenderValue,
    fillDropdownIndicator,
    size,
    width,
    menuWidth,
    isSearchable,
    customCssClass,
    ariaLabel,
    additionalSelectProps,
    selectOptionHandler,
    menuHeader,
    defaultIsOpen,
  } = props;

  const selectRef = useRef(null);

  // built-in styles override
  const customStyles = {
    menu: (provided) => ({
      ...provided,
      width: type === SelectTypes.Dropdown ? width : menuWidth || 'auto',
    }),

    container: (provided) => ({
      ...provided,
      width,
    }),

    input: (provided) => ({
      ...provided,
      padding: 0,
      margin: 0,
    }),
  };

  // theme styles override
  const theme = (themeP) => ({
    ...themeP,
    borderRadius: 0,
  });

  // static custom components for styling
  const customComponents = {
    DropdownIndicator,
    Control,
    SelectContainer,
  };

  // add tooltip header option with x button
  let updatedOptions = options;
  if (type === SelectTypes.Tooltip) {
    updatedOptions = options ? [{ isHeaderOption: true, label: '', value: '' }, ...options] : undefined;
    customComponents.Menu = Menu;
  }

  /**
   *
   * @param option - selected option
   * @param action - 'select-option': Selecting an option from the list
   'deselect-option': (Multiple) Deselecting an option from the list
   'remove-value': (Multiple) Removing a selected option with the remove button
   'pop-value': Removing options using backspace
   'set-value': Calling setValue from a component without an action
   'clear': Removing all selected options with the clear button
   'create-option': (Creatable) Creating a new option
   */
  const onChangeHandler = (option, action) => {
    if (selectOptionHandler && action.action === 'select-option') {
      selectOptionHandler(option);
    }
  };

  customComponents.Option = Option(customOptionRenderer);
  if (customValueRenderer) {
    customComponents.ValueContainer = ValueContainer(customValueRenderer);
  }

  let originSelectOption = null;
  useEffect(() => {
    // selectRef.current.select.getNextFocusedOption = () => null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    originSelectOption = selectRef.current.select.selectOption;
    selectRef.current.select.selectOption = (option) => {
      if (option.isHeaderOption) {
        selectRef.current.blur();
        selectRef.current.focus();
        return;
      }
      originSelectOption(option);
    };
  }, []);

  return (
    <Select
      ref={selectRef}
      onChange={onChangeHandler}
      placeholder={placeholder}
      styles={customStyles}
      options={updatedOptions}
      components={customComponents}
      isSearchable={isSearchable}
      width={width}
      menuWidth={menuWidth}
      value={value}
      defaultValue={defaultValue}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      theme={theme}
      size={size}
      tabSelectsValue={false}
      defaultMenuIsOpen={defaultIsOpen}
      isOptionSelected={customIsOptionSelected}
      classNamePrefix="rx-select"
      customCssClass={customCssClass}
      className={classnames('rx-select', {
        // eslint-disable-next-line react/destructuring-assignment
        [`rx-${props.type}`]: props.type,
      })}
      type={type}
      fillDropdownIndicator={fillDropdownIndicator}
      inputId={`rxSelectInput-${e2eName}`}
      e2eName={e2eName}
      aria-label={ariaLabel}
      tooltipAlign={tooltipAlign}
      controlShouldRenderValue={controlShouldRenderValue}
      menuHeader={menuHeader}
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...additionalSelectProps}
    />
  );
}

RxSelect.defaultProps = {
  type: SelectTypes.Dropdown, // also available type 'tooltip' with auto width
  size: 'sm',
  width: '100%',
  maxSearchInputWidth: '10rem',
  isSearchable: false,
  defaultIsOpen: false,
  tooltipAlign: 'right',
  fillDropdownIndicator: true,
  controlShouldRenderValue: true,
};

RxSelect.propTypes = {
  // array or map of any options, by default should have label and value props
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  e2eName: PropTypes.string.isRequired,
  type: PropTypes.string, // dropdown or tooltip
  value: PropTypes.object, // value for controlled component is needed
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // default selected value - should be the same type as option
  selectOptionHandler: PropTypes.func, // (option) => void option select handler
  size: PropTypes.string, // xs, sm, md
  width: PropTypes.string, // example: auto, 100%, 200px
  menuWidth: PropTypes.string, // width of menu for tooltip type
  maxSearchInputWidth: PropTypes.string, // max width of search input for tooltip type
  placeholder: PropTypes.string,
  tooltipAlign: PropTypes.string, // left or right
  getOptionValue: PropTypes.func, // (option) => string
  getOptionLabel: PropTypes.func, // (option) => string
  customOptionRenderer: PropTypes.func, // (selectProps) => react element, option data located at selectProps.data
  customValueRenderer: PropTypes.func, // (selectProps) => react element, select value located at selectProps.getValue()
  customIsOptionSelected: PropTypes.func, // (option, selectValue[]) => react element, option data located at selectProps.data
  isSearchable: PropTypes.bool,
  defaultIsOpen: PropTypes.bool,
  controlShouldRenderValue: PropTypes.bool,
  fillDropdownIndicator: PropTypes.bool, // either draw indicator background
  customCssClass: PropTypes.string, // additional css class for container
  ariaLabel: PropTypes.string,
  menuHeader: PropTypes.string, // label for tooltip type header
  additionalSelectProps: PropTypes.object, // any additional react-select component props
};
